import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUBSCRIPTION, ADDONS, INVOICE_STATUS } from '../constants/routes';
import AuthService from '../services/authService';
import localStorageService from '../services/localStorageService';
import subscriptionCostService, { parseSubscriptionCost } from '../services/subscriptionCostService';
import TermsConditionsModel from '../components/TermsConditionsModel';
import PurchaseStepper from '../components/PurchaseStepper';
import api from '../utils/api';
import { AuthContext } from '../store/ContextStore';
import { normalizeUserFromSubscription } from '../services/trialService';
import {
  PRIVACY_POLICY_ACCEPTED_KEY,
  PRIVACY_POLICY_VERSION_KEY,
  TERMS_ACCEPTED_KEY,
  TERMS_VERSION_KEY,
  CURRENT_POLICY_VERSION,
  CONSENTS_ACCEPT,
} from '../services/policyService';

interface SelectedSport {
  id: string;
  name: string;
  icon: string;
  gender: 'M' | 'W' | null;
}

interface AddonState {
  [sportId: string]: {
    statCrew: boolean;
    seasons: string[];
  };
}

type AddonMeta =
  | { status: 'idle' }
  | { status: 'loading'; sportCode: string }
  | {
      status: 'available';
      sportCode: string;
      addonTypeId: number;
      addonName: string;
      seasons: string[];
      ownedSeasons?: string[];
      costPerSeason: number;
      seasonPrices: Record<string, number>;
    }
  | { status: 'none'; sportCode: string }
  | { status: 'error'; sportCode: string; message: string };

// Special 'all' identifier
const ALL_SEASONS = 'all';

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authService = useMemo(() => new AuthService(), []);

  const selectedSports: SelectedSport[] = location.state?.selectedSports || [];
  const addons: AddonState = location.state?.addons || {};
  const addonMetaBySportId: Record<string, AddonMeta> = location.state?.addonMetaBySportId || {};
  const isAddonOnly: boolean = Boolean(location.state?.isAddonOnly);
  const [basePriceBySportId, setBasePriceBySportId] = useState<Record<string, number>>(
    location.state?.basePriceBySportId || {}
  );
  const [currency, setCurrency] = useState<string | null>(location.state?.currency || null);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [invoiceUsername, setInvoiceUsername] = useState('');
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [invoiceBlockingStatus, setInvoiceBlockingStatus] = useState<null | { title: string; subtitle?: string }>(null);

  const getBasePrice = (sportId: string): number => {
    const v = Number(basePriceBySportId?.[sportId]);
    return Number.isFinite(v) ? v : NaN;
  };

  const sportIdToCode: Record<string, string> = useMemo(
    () => ({
      football: 'MFB',
      'basketball-m': 'MBB',
      'basketball-w': 'WBB',
      'ice-hockey-m': 'MIH',
      'ice-hockey-w': 'WIH',
      'soccer-m': 'MSO',
      'soccer-w': 'WSO',
      'volleyball-m': 'MVB',
      'volleyball-w': 'WVB',
    }),
    []
  );

  type EntitlementStatus = 'subscribed' | 'in_trial' | 'trial_expired' | 'none';

  const resolvedUser = useMemo(() => {
    const contextUser = authCtx?.user;
    return contextUser && Object.keys(contextUser).length > 0 ? contextUser : localStorageService.getUserInfo();
  }, [authCtx?.user]);

  const subscription = (resolvedUser as any)?.subscription;
  const sportAccess: Record<string, any> = subscription?.sport_access ?? subscription?.sportAccess ?? {};

  const teamSubStatusRaw =
    subscription?.sub_status ??
    subscription?.subStatus ??
    subscription?.team_sub_status ??
    subscription?.teamSubStatus;

  const isTeamSubscribed = useMemo(() => {
    if (teamSubStatusRaw === undefined || teamSubStatusRaw === null) return false;
    const v = String(teamSubStatusRaw).trim().toLowerCase();
    return ['active', 'subscribed', 'paid', 'succeeded', 'success'].includes(v);
  }, [teamSubStatusRaw]);

  const getEntitlementStatus = useCallback(
    (sportId: string, nowMs: number = Date.now()): EntitlementStatus => {
      const sportCode = sportIdToCode[sportId];
      if (!sportCode) return 'none';

      const row = sportAccess?.[sportCode];
      if (!row || typeof row !== 'object') {
        return isTeamSubscribed ? 'subscribed' : 'none';
      }

      const status = String(row?.status || '').trim().toLowerCase();
      const untilSec = Number(row?.until);
      const nowSec = Math.floor(nowMs / 1000);

      if (status === 'locked') return 'none';
      if (status === 'trial_end') return 'trial_expired';
      if (status === 'trial') {
        if (Number.isFinite(untilSec) && untilSec > 0 && untilSec <= nowSec) return 'trial_expired';
        return 'in_trial';
      }
      if (['active', 'subscribed', 'paid', 'succeeded', 'success'].includes(status)) return 'subscribed';
      if (isTeamSubscribed) return 'subscribed';

      return 'none';
    },
    [isTeamSubscribed, sportAccess, sportIdToCode]
  );

  const getEffectiveBasePrice = useCallback(
    (sportId: string): number => {
      if (isAddonOnly) return 0;
      const entitlement = getEntitlementStatus(sportId);
      if (entitlement === 'subscribed') return 0;
      return getBasePrice(sportId);
    },
    [getEntitlementStatus, getBasePrice, isAddonOnly]
  );

  const baseSportsCount = isAddonOnly
    ? 0
    : selectedSports.filter((s) => (Number(getEffectiveBasePrice(s.id)) || 0) > 0).length;
  const baseCost = isAddonOnly
    ? 0
    : selectedSports.reduce((sum, s) => sum + (Number(getEffectiveBasePrice(s.id)) || 0), 0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    // Check if user has already accepted terms - pre-check the checkbox if accepted
    const userData = localStorageService.getUserInfo();
    const privacyTermsAccepted = userData?.privacy_terms === true;
    if (privacyTermsAccepted) {
      setTermsAccepted(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function ensurePricing() {
      if (!selectedSports.length) return;
      const missingPrices = selectedSports.filter((s) => !Number.isFinite(Number(basePriceBySportId?.[s.id])));
      const needsCurrency = !currency;
      if (missingPrices.length === 0 && !needsCurrency) return;

      setPricingLoading(true);
      setPricingUnavailable(false);

      const sportsToFetch = missingPrices.length > 0 ? missingPrices : selectedSports.slice(0, 1);
      for (const sport of sportsToFetch) {
        const sportCode = sportIdToCode[sport.id];
        if (!sportCode) {
          if (!cancelled) setPricingUnavailable(true);
          continue;
        }

        const entitlement = getEntitlementStatus(sport.id);
        if (entitlement === 'subscribed') {
          setBasePriceBySportId((prev) => {
            if (prev[sport.id] === 0) return prev;
            return { ...prev, [sport.id]: 0 };
          });
          // Still fetch once for currency if missing.
          if (!needsCurrency) continue;
        }

        if (isAddonOnly) {
          setBasePriceBySportId((prev) => {
            if (prev[sport.id] === 0) return prev;
            return { ...prev, [sport.id]: 0 };
          });
          // Still fetch once for currency if missing.
          if (!needsCurrency) continue;
        }

        const res: any = await subscriptionCostService.getSubscriptionCost(sportCode);
        const status = res?.status;
        if (cancelled) return;

        if (status === 404) {
          setPricingUnavailable(true);
          continue;
        }
        if (!status || status < 200 || status >= 300) {
          setPricingUnavailable(true);
          continue;
        }

        const { cost, currency: cur } = parseSubscriptionCost(res?.data);
        if (!cur && cost === null) {
          setPricingUnavailable(true);
          continue;
        }

        if (!isAddonOnly && entitlement !== 'subscribed' && cost !== null) {
          setBasePriceBySportId((prev) => {
            if (prev[sport.id] === cost) return prev;
            return { ...prev, [sport.id]: cost };
          });
        }
        if (cur) {
          setCurrency((prev) => {
            if (prev === cur) return prev;
            return cur;
          });
        }
      }

      setPricingLoading(false);
    }

    void ensurePricing();
    return () => {
      cancelled = true;
    };
  }, [basePriceBySportId, currency, getEntitlementStatus, isAddonOnly, selectedSports, sportIdToCode]);

  const getSeasonsForSport = (sportId: string): string[] => {
    const meta = addonMetaBySportId[sportId];
    return meta?.status === 'available' ? meta.seasons : [];
  };

  const getOwnedSeasonsForSport = (sportId: string): string[] => {
    const meta = addonMetaBySportId[sportId];
    return meta?.status === 'available' ? (meta.ownedSeasons || []) : [];
  };

  const getEffectiveSeasonCount = (sportId: string): number => {
    const addon = addons[sportId];
    if (!addon?.statCrew || !addon?.seasons?.length) return 0;
    const owned = new Set(getOwnedSeasonsForSport(sportId));
    if (addon.seasons.includes(ALL_SEASONS)) {
      return getSeasonsForSport(sportId).filter((s) => !owned.has(String(s))).length;
    }
    return addon.seasons.filter((s) => !owned.has(String(s))).length;
  };

  const getSelectedSeasons = (sportId: string): string[] => {
    const selected = addons[sportId]?.seasons || [];
    const owned = new Set(getOwnedSeasonsForSport(sportId));
    if (selected.includes(ALL_SEASONS)) {
      return getSeasonsForSport(sportId).filter((s) => !owned.has(String(s)));
    }
    return selected.filter((s) => s !== ALL_SEASONS && !owned.has(String(s)));
  };

  const getAddonCost = (sportId: string): number => {
    const addon = addons[sportId];
    const meta = addonMetaBySportId[sportId];
    if (!addon?.statCrew) return 0;
    if (meta?.status !== 'available') return 0;

    const seasons = getSelectedSeasons(sportId);
    if (seasons.length === 0) return 0;

    return seasons.reduce(
      (sum, s) => sum + (Number(meta.seasonPrices?.[s]) || Number(meta.costPerSeason) || 0),
      0
    );
  };

  const totalAddonCost = selectedSports.reduce((sum, sport) => sum + getAddonCost(sport.id), 0);

  const finalTotal = baseCost + totalAddonCost;

  const sportsForDisplay: SelectedSport[] = useMemo(() => {
    if (isAddonOnly) {
      return selectedSports.filter((s) => addons[s.id]?.statCrew && getEffectiveSeasonCount(s.id) > 0);
    }

    // In the normal (non-addon-only) flow, only show sports that actually contribute to the bill:
    // - base subscription amount > 0, OR
    // - addon amount > 0
    return selectedSports.filter((s) => {
      const base = Number(getEffectiveBasePrice(s.id)) || 0;
      const addon = Number(getAddonCost(s.id)) || 0;
      return base > 0 || addon > 0;
    });
  }, [addons, getAddonCost, getEffectiveBasePrice, getEffectiveSeasonCount, isAddonOnly, selectedSports]);

  // Check if user has already accepted terms (from previous agreement)
  const userPreviouslyAccepted = localStorageService.getUserInfo()?.privacy_terms === true;
  // Show checkbox only if user hasn't previously accepted
  const showTermsCheckbox = !userPreviouslyAccepted;

  const handleGenerateInvoice = async () => {
    setInvoiceError(null);
    setIsGeneratingInvoice(true);
    setInvoiceBlockingStatus({ title: 'Generating invoice and sending to mail', subtitle: 'Please wait…' });

    try {
      if (pricingLoading) {
        setInvoiceError('Fetching pricing… please wait.');
        return;
      }
      if (pricingUnavailable) {
        setInvoiceError('Pricing unavailable, contact support.');
        return;
      }
      if (!currency) {
        setInvoiceError('Missing currency. Pricing unavailable, contact support.');
        return;
      }
      if (!invoiceEmail.trim()) {
        setInvoiceError('Email is required to generate an invoice.');
        return;
      }
      if (!invoiceUsername.trim()) {
        setInvoiceError('Username is required to generate an invoice.');
        return;
      }

      const userInfo = localStorageService.getUserInfo();
      const billingUserId = Number(userInfo?.user_id || userInfo?.userId || userInfo?.id);
      if (!Number.isFinite(billingUserId)) {
        setInvoiceError('Missing billing user id. Please log in again.');
        return;
      }

      if (selectedSports.length === 0) {
        setInvoiceError('No sports selected for invoice.');
        return;
      }

      const sportCodes = selectedSports
        .map((s) => sportIdToCode[s.id])
        .filter(Boolean);

      if (sportCodes.length !== selectedSports.length) {
        setInvoiceError('One or more selected sports are not supported for checkout yet.');
        return;
      }

      const subIdResponses = await Promise.all(sportCodes.map((code) => authService.getTeamSubscriptionId(code)));
      const teamSubscriptionIds = (subIdResponses as any[]).map((res: any) => res?.data?.team_subscription_id);
      if (teamSubscriptionIds.some((id: any) => !id)) {
        setInvoiceError('Failed to fetch subscription id(s). Please try again.');
        return;
      }

      const sportIdToTeamSubscriptionId: Record<string, string> = {};
      selectedSports.forEach((s, idx) => {
        const id = teamSubscriptionIds[idx];
        if (id) sportIdToTeamSubscriptionId[s.id] = String(id);
      });

      // Build subscriptions array for invoice
      const subscriptions: any[] = [];
      for (const sport of selectedSports) {
        const sportCode = sportIdToCode[sport.id];
        if (!sportCode) continue;

        const teamSubscriptionId = String(sportIdToTeamSubscriptionId?.[sport.id] || '');
        if (!teamSubscriptionId) {
          setInvoiceError('Missing subscription id(s). Please try again in a moment.');
          return;
        }

        // Add base subscription if not addon-only
        const subscriptionAmount = !isAddonOnly ? Number(getEffectiveBasePrice(sport.id)) || 0 : 0;
        if (!isAddonOnly && subscriptionAmount > 0) {
          subscriptions.push({
            type: 'sport',
            team_subscription_id: teamSubscriptionId,
            sports_code: sportCode,
            description: `${sportCode} ${sport.name} subscription`,
            amount: Number(subscriptionAmount.toFixed(2)),
          });
        }

        // Add addon subscriptions
        if (addons[sport.id]?.statCrew) {
          const selectedSeasons = getSelectedSeasons(sport.id);
          if (selectedSeasons.length > 0) {
            const meta = addonMetaBySportId[sport.id];
            const addonTypeId = meta?.status === 'available' ? Number(meta.addonTypeId) || 1 : 1;

            selectedSeasons.forEach((season) => {
              const seasonAmount =
                meta?.status === 'available'
                  ? Number(meta.seasonPrices?.[String(season)]) || Number(meta.costPerSeason) || 0
                  : 0;
              subscriptions.push({
                type: 'addon',
                team_subscription_id: teamSubscriptionId,
                addon_type_id: addonTypeId,
                addon_config_id: null,
                sports_code: sportCode,
                selected_season: String(season),
                description: `${sportCode} ${sport.name} Stat Crew ${season}`,
                amount: Number(seasonAmount.toFixed(2)),
              });
            });
          }
        }
      }

      if (subscriptions.length === 0) {
        setInvoiceError('Nothing to invoice.');
        return;
      }

      const totalAmount = Number(
        subscriptions.reduce((sum, s) => sum + (Number(s.amount) || 0), 0).toFixed(2)
      );

      const invoicePayload = {
        email: invoiceEmail.trim(),
        username: invoiceUsername.trim(),
        currency,
        amount: totalAmount,
        billing_user_id: billingUserId,
        team_name: userInfo?.team?.name || userInfo?.team_name || 'Team',
        subscriptions,
      };

      setInvoiceBlockingStatus({ title: 'Generating invoice and sending to mail', subtitle: 'Please wait…' });

      const invoiceService = new AuthService();
      const res: any = await invoiceService.generateInvoice(invoicePayload);

      if (res?.status >= 200 && res?.status < 300) {
        // Save terms acceptance to localStorage and backend like in payment flow
        try {
          const userInfo = localStorageService.getUserInfo();
          const token = userInfo?.access_token || '';

          if (token) {
            await api.post(
              CONSENTS_ACCEPT,
              {
                privacyPolicyVersion: CURRENT_POLICY_VERSION,
                termsVersion: CURRENT_POLICY_VERSION,
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }
        } catch (error) {
          // Continue even if API call fails - localStorage update will still work
        }

        localStorage.setItem(PRIVACY_POLICY_ACCEPTED_KEY, 'true');
        localStorage.setItem(PRIVACY_POLICY_VERSION_KEY, CURRENT_POLICY_VERSION);
        localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
        localStorage.setItem(TERMS_VERSION_KEY, CURRENT_POLICY_VERSION);

        // Update user data in localStorage
        const existingUser = localStorageService.getUserInfo();
        const updatedUser = { ...existingUser, privacy_terms: true };
        localStorage.setItem('sid_user', JSON.stringify(updatedUser));

        // Refresh session (auth/refresh) so subscription/access updates are reflected immediately.
        // Note: This intentionally hits refresh instead of /api/profile.
        try {
          const userInfo = localStorageService.getUserInfo();
          const refreshToken: string = userInfo?.refresh_token || userInfo?.refreshToken || '';
          if (refreshToken) {
            const refreshRes: any = await authService.RefreshAuth(refreshToken);
            if (refreshRes?.status >= 200 && refreshRes?.status < 300) {
              const refreshPayload = refreshRes?.data ?? refreshRes;
              const newUserData = refreshPayload?.user || refreshPayload;
              if (newUserData && Object.keys(newUserData).length > 0) {
                const mergedUser = normalizeUserFromSubscription({
                  ...userInfo,
                  ...newUserData,
                  access_token: refreshPayload?.access_token || userInfo?.access_token,
                  refresh_token: refreshPayload?.refresh_token || userInfo?.refresh_token,
                  token: refreshPayload?.access_token || userInfo?.token,
                });
                localStorage.setItem('sid_user', JSON.stringify(mergedUser));

                // Keep in-memory AuthContext in sync so Header updates immediately (no hard refresh needed).
                if (authCtx?.setUser) authCtx.setUser(mergedUser);
                if (authCtx?.setTeam) authCtx.setTeam(mergedUser?.team || {});

                const sportCodes: string[] = Array.isArray((mergedUser as any)?.sports)
                  ? (mergedUser as any).sports.map((s: any) => s?.code).filter(Boolean)
                  : [];
                if (sportCodes.length > 0 && authCtx?.setSelectedSport) {
                  const currentSelected = localStorageService.getSelectedSport('');
                  const nextSelected = sportCodes.includes('MFB')
                    ? 'MFB'
                    : (sportCodes.includes(currentSelected) ? currentSelected : sportCodes[0]);
                  localStorageService.setSelectedSport(nextSelected);
                  authCtx.setSelectedSport(nextSelected);
                }
              }
            }
          }
        } catch (error) {
          // Best-effort refresh - continue even if it fails
        }

        setInvoiceBlockingStatus({ title: 'Invoice generated successfully!', subtitle: 'Redirecting to invoice status…' });
        setTimeout(() => {
          navigate(INVOICE_STATUS, { state: { invoiceResponseData: res?.data, subscriptionDetails: subscriptions } });
        }, 1500);
      } else {
        const errorMsg = res?.data?.detail || res?.data?.message || 'Failed to generate invoice';
        setInvoiceError(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to generate invoice';
      setInvoiceError(errorMsg);
    } finally {
      setIsGeneratingInvoice(false);
      setInvoiceBlockingStatus(null);
    }
  };

  const handlePayment = async () => {
    // Check if user has accepted terms - required for first-time agreement
    const userData = localStorageService.getUserInfo();
    const privacyTermsAccepted = userData?.privacy_terms === true;

    // If terms not previously accepted and checkbox not checked, block payment
    if (!privacyTermsAccepted && !termsAccepted) {
      setError('Please accept the Terms and Conditions to proceed with payment.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (pricingLoading) {
        setError('Fetching pricing… please wait.');
        return;
      }
      if (pricingUnavailable) {
        setError('Pricing unavailable, contact support.');
        return;
      }
      if (!currency) {
        setError('Missing currency. Pricing unavailable, contact support.');
        return;
      }

      const sportCodes = selectedSports
        .map((s) => sportIdToCode[s.id])
        .filter(Boolean);

      if (sportCodes.length !== selectedSports.length) {
        setError('One or more selected sports are not supported for checkout yet.');
        return;
      }

      const subIdResponses = await Promise.all(sportCodes.map((code) => authService.getTeamSubscriptionId(code)));
      const teamSubscriptionIds = (subIdResponses as any[]).map((res: any) => res?.data?.team_subscription_id);

      if (teamSubscriptionIds.some((id: any) => !id)) {
        setError('Failed to fetch subscription ids. Please try again.');
        return;
      }

      const sportIdToTeamSubscriptionId: Record<string, string> = {};
      selectedSports.forEach((s, idx) => {
        const id = teamSubscriptionIds[idx];
        if (id) sportIdToTeamSubscriptionId[s.id] = id;
      });

      const addonPayload = Object.entries(addons)
        .filter(([, a]) => a?.statCrew)
        .map(([sportId]) => {
          const teamSubscriptionId = sportIdToTeamSubscriptionId[sportId];
          if (!teamSubscriptionId) return null;

          const meta = addonMetaBySportId[sportId];
          const selectedSeasons = getSelectedSeasons(sportId);
          if (selectedSeasons.length === 0) return null;
          if (addons[sportId]?.seasons?.includes(ALL_SEASONS) && meta?.status !== 'available') return null;
          const addonTypeId = meta?.status === 'available' ? Number(meta.addonTypeId) || 1 : 1;

          return {
            team_subscription_id: teamSubscriptionId,
            addon_type_id: addonTypeId,
            selected_seasons: selectedSeasons,
            details: 'Stat Crew purchase',
          };
        })
        .filter(Boolean);

      const userInfo = localStorageService.getUserInfo();
      const billingUserId = Number(userInfo?.user_id || userInfo?.userId || userInfo?.id);
      if (!Number.isFinite(billingUserId)) {
        setError('Missing billing user id. Please log in again.');
        return;
      }

      const payload = {
        team_subscription_ids: teamSubscriptionIds,
        billing_user_id: billingUserId,
        amount: Number(finalTotal.toFixed(2)),
        currency,
        currency_code: currency,
        addons: addonPayload,
      };

      const sessionRes: any = await authService.createStripeSession(payload);
      const redirectUrl =
        sessionRes?.data?.checkout_url ||
        sessionRes?.data?.url ||
        sessionRes?.data?.checkoutUrl;

      if (sessionRes?.status >= 200 && sessionRes?.status < 300 && redirectUrl) {
        // Save privacy_terms acceptance AFTER successful payment - both localStorage and backend
        try {
          const userInfo = localStorageService.getUserInfo();
          const token = userInfo?.access_token || '';

          if (token) {
            await api.post(
              CONSENTS_ACCEPT,
              {
                privacyPolicyVersion: CURRENT_POLICY_VERSION,
                termsVersion: CURRENT_POLICY_VERSION,
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }
        } catch (error) {
          // Continue even if API call fails - localStorage update will still work
        }

        localStorage.setItem(PRIVACY_POLICY_ACCEPTED_KEY, 'true');
        localStorage.setItem(PRIVACY_POLICY_VERSION_KEY, CURRENT_POLICY_VERSION);
        localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
        localStorage.setItem(TERMS_VERSION_KEY, CURRENT_POLICY_VERSION);

        // Update user data in localStorage
        const existingUser = localStorageService.getUserInfo();
        const updatedUser = { ...existingUser, privacy_terms: true };
        localStorage.setItem('sid_user', JSON.stringify(updatedUser));

        window.location.href = redirectUrl;
        return;
      }

      const msg =
        sessionRes?.data?.detail ||
        sessionRes?.data?.message ||
        'Failed to start checkout. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Failed to start checkout. Please try again.');
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to start checkout. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Failed to start checkout. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
  <div className="min-h-screen w-full bg-slate-50">
    {/* Back Button */}
    <div className="max-w-6xl mx-auto px-4 pt-6 flex items-start">
      <button
        onClick={() => navigate(ADDONS, { state: { selectedSports, addons, addonMetaBySportId, basePriceBySportId, currency, isAddonOnly } })}
        className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
        aria-label="Go back to addons"
      >
        <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </div>
    <div className="max-w-4xl mx-auto px-4 py-8">

      <PurchaseStepper currentStep={3} className="mb-8" />

      <div className="mb-2">
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Order review</h1>
        <p className="text-slate-600">Verify your sports selection and complete your Upgrade</p>
      </div>

      {/* Sports List Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <span></span> Selected sports
        </h2>
        {sportsForDisplay.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-500 mb-3">No addons selected yet</p>
            <button onClick={() => navigate(ADDONS)} className="text-red-500 hover:text-red-600 font-medium text-sm">
              Go back to Addons
            </button>
          </div>
        ) : (
          <div className="space-y-3">
	            {sportsForDisplay.map((sport) => {
	              const addon = addons[sport.id];
	              const seasonCount = getEffectiveSeasonCount(sport.id);
	              const addonCost = getAddonCost(sport.id);
	              const basePrice = getEffectiveBasePrice(sport.id);
	              const meta = addonMetaBySportId[sport.id];
	              const isAllSelected = addon?.seasons?.includes(ALL_SEASONS) || false;

	              const selectedAddonSeasons = addon?.statCrew ? getSelectedSeasons(sport.id) : [];
	              const perSeasonPrices =
	                meta?.status === 'available'
	                  ? selectedAddonSeasons.map((s) => Number(meta.seasonPrices?.[s]) || Number(meta.costPerSeason) || 0)
	                  : [];
	              const first = perSeasonPrices[0];
	              const hasUniformPrice =
	                perSeasonPrices.length > 0 &&
	                perSeasonPrices.every((p) => Number.isFinite(p) && p === first) &&
	                Number.isFinite(first);

	              return (
	                <div key={sport.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
	                  <div className="flex items-center gap-4 flex-1">
	                    <div className="text-4xl">{sport.icon}</div>
	                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{sport.name}</p>
                      {sport.gender && (
                        <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${sport.gender === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-700'}`}>
                          {sport.gender === 'M' ? "Men's league" : "Women's league"}
                        </span>
	                      )}
	                      {addon?.statCrew && seasonCount > 0 && (
	                        <p className="text-xs text-slate-500 mt-1">
	                          Stat Crew: {isAllSelected ? 'All ' : ''}
	                          {selectedAddonSeasons.join(', ')}
	                        </p>
	                      )}
	                    </div>
	                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">
                      {currency ? `${((Number(basePrice) || 0) + addonCost).toFixed(2)} ${currency}` : `$${((Number(basePrice) || 0) + addonCost).toFixed(2)}`}
                    </p>
                    <p className="text-xs text-slate-500"></p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Order Summary & Actions Panel ── */}
      {sportsForDisplay.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4">
            {isAddonOnly ? 'Addons summary' : 'Order summary'}
          </h3>

          {pricingUnavailable && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              Pricing unavailable, contact support.
            </div>
          )}

          {/* Price rows */}
          <div className="space-y-2 mb-4">
            {baseSportsCount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{baseSportsCount} sport{baseSportsCount !== 1 ? 's' : ''}</span>
                <span className="font-medium text-slate-900">{currency ? `${baseCost} ${currency}` : `$${baseCost}`}</span>
              </div>
            )}
            {totalAddonCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Addons ({sportsForDisplay.filter(s => addons[s.id]?.statCrew && getEffectiveSeasonCount(s.id) > 0).length} sports)</span>
                <span className="font-medium text-slate-900">{totalAddonCost} USD</span>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 mb-4" />

          {/* Total */}
          <div className="flex justify-between items-baseline mb-6">
            <span className="text-sm text-slate-600">Total due</span>
            <span className="text-2xl font-bold text-slate-900">
              {currency ? `${finalTotal.toFixed(2)} ${currency}` : `$${finalTotal.toFixed(2)}`}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-6">Billed annually</p>

          {/* Terms checkbox - SINGLE GLOBAL */}
          {showTermsCheckbox && (
            <div className="mb-6 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-red-500 focus:ring-red-500 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="terms-checkbox" className="text-xs text-slate-600 cursor-pointer">
                I have read and agree to the{' '}
                <button onClick={() => setShowTermsModal(true)} className="text-blue-600 hover:text-blue-700 underline font-medium">
                  Terms and Conditions
                </button>
              </label>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          {/* Action Buttons Section */}
          <div className="space-y-4">
            {/* Button Row: Payment | OR | Invoice */}
            <div className="flex items-center gap-3">
              {/* Proceed to Payment Button */}
              <button
                onClick={handlePayment}
                disabled={isProcessing || pricingLoading || pricingUnavailable || !currency || (showTermsCheckbox && !termsAccepted)}
                className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>Processing...</>
                ) : (
                  <>Proceed to payment</>
                )}
              </button>

              {/* OR Divider */}
              <div className="relative flex items-center">
                <div className="w-px h-12 bg-slate-200" />
                <span className="absolute left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-semibold text-slate-400 tracking-wider border border-slate-200 rounded-full bg-white">
                  OR
                </span>
              </div>

              {/* Generate Invoice Button */}
              <button
                onClick={() => setShowInvoiceModal(true)}
                disabled={pricingLoading || pricingUnavailable || !currency || (showTermsCheckbox && !termsAccepted)}
                className="flex-1 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-100 disabled:cursor-not-allowed"
              >
                Generate Invoice
              </button>
            </div>
          </div>

          {/* Add more sports button */}
          <button
            onClick={() => navigate(SUBSCRIPTION, { state: { selectedSports, addons, addonMetaBySportId, basePriceBySportId, currency, isAddonOnly } })}
            className="w-full mt-4 py-2.5 bg-gray-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-colors text-sm"
          >
            + Add more sports
          </button>

          {/* Security badge */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <span>🛡️</span> Secure &amp; encrypted &nbsp;·&nbsp; Your payment info is safe
            </p>
          </div>
        </div>
      )}
    </div>

    {/* Invoice Modal */}
    {showInvoiceModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-slate-900/50" onClick={() => !isGeneratingInvoice && setShowInvoiceModal(false)} />
        
        {/* Modal */}
        <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Generate Invoice</h3>
          <p className="text-sm text-slate-600 mb-4">Fill in your details to generate and send invoice to your email</p>
          
          <div className="space-y-3 mb-4">
            <div>
              <label htmlFor="modal-invoice-email" className="block text-xs font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="modal-invoice-email"
                type="email"
                placeholder="name@school.edu"
                value={invoiceEmail}
                onChange={(e) => setInvoiceEmail(e.target.value)}
                disabled={isGeneratingInvoice}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-60"
              />
            </div>
            
            <div>
              <label htmlFor="modal-invoice-username" className="block text-xs font-medium text-slate-700 mb-1">
                Username
              </label>
              <input
                id="modal-invoice-username"
                type="text"
                placeholder="username"
                value={invoiceUsername}
                onChange={(e) => setInvoiceUsername(e.target.value)}
                disabled={isGeneratingInvoice}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-60"
              />
            </div>
          </div>

          {invoiceError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
              {invoiceError}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setShowInvoiceModal(false)}
              disabled={isGeneratingInvoice}
              className="flex-1 py-2 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || !invoiceEmail.trim() || !invoiceUsername.trim()}
              className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGeneratingInvoice ? (
                <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>Generating...</>
              ) : (
                <>Continue</>
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Invoice Blocking Status */}
    {invoiceBlockingStatus && (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <span className="inline-block w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">{invoiceBlockingStatus.title}</div>
              {invoiceBlockingStatus.subtitle && (
                <div className="text-xs text-slate-500 mt-0.5">{invoiceBlockingStatus.subtitle}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {showTermsModal && (
      <TermsConditionsModel
        onClose={() => setShowTermsModal(false)}
        onAccept={() => { setTermsAccepted(true); setShowTermsModal(false); }}
        isLoading={false}
      />
    )}
  </div>
);
}

export default Checkout;
